import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

// Facebook Pixel declaration
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}

interface Partner {
  id: string;
  name: string;
  logoBase64: string;
  isActive: boolean;
  order: number;
}

// 📱 Ultra-Realistic iPhone 15 Pro Max Component in Pure CSS
const RealisticIPhone: React.FC<{ imgSrc: string; label: string; rotateDeg?: number; marginTop?: string }> = ({ imgSrc, label, rotateDeg = 0, marginTop = '0px' }) => {
  return (
    <motion.div 
      className="realistic-iphone-device"
      style={{
        position: 'relative',
        width: '260px',
        height: '520px',
        background: '#1a1a1a', // Space gray premium chassis
        borderRadius: '38px',
        padding: '8px',
        boxShadow: '0 25px 60px rgba(120, 53, 15, 0.25), inset 0 0 4px rgba(255, 255, 255, 0.25)',
        border: '3px solid #3A3A3C', // Matte space gray metallic outer bezel
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        transform: `rotate(${rotateDeg}deg)`,
        marginTop: marginTop,
        zIndex: 2,
        overflow: 'visible',
        flexShrink: 0
      }}
      whileHover={{ scale: 1.05, rotate: 0, zIndex: 10 }}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
    >
      {/* 🔘 Physical Hardware Buttons */}
      {/* Left side: Volume Keys & Action Button */}
      <div style={{ position: 'absolute', left: '-5px', top: '75px', width: '2px', height: '18px', background: '#3A3A3C', borderRadius: '2px 0 0 2px' }}></div>
      <div style={{ position: 'absolute', left: '-5px', top: '105px', width: '2px', height: '32px', background: '#3A3A3C', borderRadius: '2px 0 0 2px' }}></div>
      <div style={{ position: 'absolute', left: '-5px', top: '145px', width: '2px', height: '32px', background: '#3A3A3C', borderRadius: '2px 0 0 2px' }}></div>
      
      {/* Right side: Power Button */}
      <div style={{ position: 'absolute', right: '-5px', top: '120px', width: '2px', height: '48px', background: '#3A3A3C', borderRadius: '0 2px 2px 0' }}></div>

      {/* Screen Frame Bezel (Modern thin bezels) */}
      <div style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        background: '#000',
        borderRadius: '30px',
        padding: '3px', // Ultra-thin bezel
        overflow: 'hidden',
        boxShadow: 'inset 0 0 8px rgba(0,0,0,0.9)'
      }}>
        {/* Dynamic Island Capsule */}
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '70px',
          height: '16px',
          background: '#000',
          borderRadius: '10px',
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          paddingRight: '10px',
          boxShadow: 'inset 0 0 1px rgba(255,255,255,0.1)'
        }}>
          {/* Camera Lens Spec */}
          <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#111', border: '1px solid #222', marginRight: '3px' }}></div>
          <div style={{ width: '3px', height: '3px', borderRadius: '50%', background: '#08081a' }}></div>
        </div>

        {/* Screen Image Display Area */}
        <div style={{
          width: '100%',
          height: '100%',
          borderRadius: '27px',
          overflow: 'hidden',
          background: '#000',
          position: 'relative'
        }}>
          <img 
            src={imgSrc} 
            alt={label} 
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover',
              display: 'block'
            }} 
          />

          {/* Screen Light Glare Highlight overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '200%',
            height: '200%',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,0) 100%)',
            pointerEvents: 'none',
            zIndex: 10
          }}></div>
        </div>
      </div>

      {/* Smartphone Label Badge */}
      <div style={{
        position: 'absolute',
        bottom: '-32px',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'rgba(120, 53, 15, 0.95)',
        color: '#FFF',
        padding: '4px 12px',
        borderRadius: '10px',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
        boxShadow: '0 4px 10px rgba(120, 53, 15, 0.25)',
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        {label}
      </div>
    </motion.div>
  );
};

const CafePartnershipPage: React.FC = () => {
  const [formData, setFormData] = useState({
    cafeName: '',
    ownerName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    district: '',
    otherCity: '',
    website: '',
    instagram: '',
    privacy: false,
    marketing: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [partners, setPartners] = useState<Partner[]>([]);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await fetch('https://api.sobcof.com/api/partners');
      const result = await response.json();

      if (result.success) {
        setPartners(result.data);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      if (name === 'city') {
        setFormData(prev => ({
          ...prev,
          [name]: value,
          otherCity: value === 'Diğer' ? prev.otherCity : ''
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.cafeName.trim()) newErrors.cafeName = 'Kafe adı zorunludur';
    if (!formData.ownerName.trim()) newErrors.ownerName = 'İşletmeci adı zorunludur';
    if (!formData.email.trim()) newErrors.email = 'E-posta adresi zorunludur';
    if (!formData.phone.trim()) newErrors.phone = 'Telefon numarası zorunludur';
    if (!formData.address.trim()) newErrors.address = 'Adres zorunludur';
    if (!formData.city) newErrors.city = 'Şehir seçimi zorunludur';
    if (!formData.district.trim()) newErrors.district = 'İlçe zorunludur';
    if (formData.city === 'Diğer' && !formData.otherCity?.trim()) {
      newErrors.otherCity = 'Lütfen şehir adını yazın';
    }
    if (!formData.privacy) newErrors.privacy = 'Gizlilik Politikası ve Partnerlik Koşulları\'nı kabul etmelisiniz';

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Geçerli bir e-posta adresi girin';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      const firstErrorElement = document.querySelector('.error-field');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const submitData = {
        ...formData,
        city: formData.city === 'Diğer' ? formData.otherCity : formData.city,
        cafeType: 'Belirtilmedi',
        capacity: 'Belirtilmedi',
        averagePrice: 'Belirtilmedi',
        specialFeatures: [],
        photoURL: ''
      };

      const response = await fetch('https://api.sobcof.com/api/cafe-applications', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      const result = await response.json();

      if (result.success && result.data?.id) {
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'CompleteRegistration', {
            content_name: 'Cafe Partnership Application',
            status: 'submitted'
          });
        }

        setSubmitStatus('success');

        setTimeout(() => {
          const successElement = document.getElementById('success-message');
          if (successElement) {
            successElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);

        setFormData({
          cafeName: '',
          ownerName: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          district: '',
          otherCity: '',
          website: '',
          instagram: '',
          privacy: false,
          marketing: false
        });
      } else {
        console.error('Cafe partnership application error:', result.message);
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Cafe partnership application error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="website-container" style={{ backgroundColor: '#FAF8F5', overflowX: 'hidden' }}>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Kafe Partnerliği | SobCof - Kafenizi Binlerce Kahve Severle Buluşturun</title>
        <meta name="description" content="SobCof kafe partnerliği ile kafenizi Türkiye'nin en büyük kahve topluluğuna tanıtın. Ücretsiz pazarlama, yeni müşteri kazanımı ve işinizi büyütme fırsatları. Hemen başvurun!" />
        <meta name="keywords" content="kafe partnerliği, kafe işbirliği, kahve kafe, kafe pazarlama, kafe tanıtım, sobcof partner, kafe başvuru, kafe ortaklık, coffee shop partnership, kafe reklam, kafe sosyal medya" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sobcof.com/cafe-partnership" />
        <meta property="og:title" content="Kafe Partnerliği | SobCof - Kafenizi Binlerce Kahve Severle Buluşturun" />
        <meta property="og:description" content="SobCof kafe partnerliği ile kafenizi Türkiye'nin en büyük kahve topluluğuna tanıtın. Ücretsiz pazarlama ve yeni müşteri kazanımı fırsatları." />
        <meta property="og:image" content="https://sobcof.com/assets/LogoBrown.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="SobCof Kafe Partnerliği - Kafenizi Tanıtın" />
        <meta property="og:site_name" content="SobCof" />
        <meta property="og:locale" content="tr_TR" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://sobcof.com/cafe-partnership" />
        <meta property="twitter:title" content="Kafe Partnerliği | SobCof - Kafenizi Binlerce Kahve Severle Buluşturun" />
        <meta property="twitter:description" content="SobCof kafe partnerliği ile kafenizi Türkiye'nin en büyük kahve topluluğuna tanıtın. Ücretsiz pazarlama ve yeni müşteri kazanımı fırsatları." />
        <meta property="twitter:image" content="https://sobcof.com/assets/LogoBrown.png" />
        <meta property="twitter:image:alt" content="SobCof Kafe Partnerliği - Kafenizi Tanıtın" />

        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <meta name="author" content="SobCof" />
        <meta name="language" content="Turkish" />
        <meta name="geo.region" content="TR" />
        <meta name="geo.placename" content="Türkiye" />

        <link rel="canonical" href="https://sobcof.com/cafe-partnership" />
      </Helmet>

      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="nav-logo">
            <a href="/" className="logo-link">
              <img src="/assets/LogoBrown.png" alt="SobCof Logo" className="logo-img" />
            </a>
          </div>
          <div className={`nav-menu ${mobileMenuOpen ? 'active' : ''}`}>
            <a href="/" className="nav-link">Ana Sayfa</a>
            <a href="/#features" className="nav-link">Özellikler</a>
            <a href="/#how-it-works" className="nav-link">Nasıl Çalışır</a>
            <a href="/#download" className="nav-link">İndir</a>
            <a href="/contact" className="nav-link">İletişim</a>
            <a href="/cafe-partnership" className="nav-link">Kafe Partnerliği</a>
            <a href="/cafe-partnership/login" className="nav-link" style={{
              backgroundColor: '#78350F',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '6px',
              fontWeight: '600'
            }}>
              <i className="fas fa-sign-in-alt" style={{ marginRight: '6px' }}></i>
              Kafe Girişi
            </a>
          </div>
          <div className="nav-toggle" onClick={toggleMobileMenu}>
            <i className={`fas ${mobileMenuOpen ? 'fa-times' : 'fa-bars'}`}></i>
          </div>
        </div>
      </nav>

      {/* WhatsApp Button */}
      <a
        href="https://wa.me/905525550809"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        title="WhatsApp ile İletişime Geçin"
      >
        <i className="fab fa-whatsapp"></i>
      </a>

      {/* Magnified & Striking Hero Section */}
      <section className="partnership-hero" style={{
        position: 'relative',
        background: 'linear-gradient(135deg, #FAF8F5 0%, #F5EDE3 100%)',
        overflow: 'hidden'
      }}>
        {/* Soft Glowing Background Blur Blobs for High-End Aesthetic */}
        <div style={{
          position: 'absolute',
          top: '-20%',
          left: '-10%',
          width: '50vw',
          height: '50vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(120, 53, 15, 0.08) 0%, rgba(251, 191, 36, 0.04) 50%, transparent 100%)',
          filter: 'blur(60px)',
          zIndex: 1
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '-10%',
          right: '-10%',
          width: '45vw',
          height: '45vw',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(210, 105, 30, 0.08) 0%, rgba(251, 191, 36, 0.04) 50%, transparent 100%)',
          filter: 'blur(60px)',
          zIndex: 1
        }}></div>

        <div className="container hero-container" style={{
          position: 'relative',
          zIndex: 2,
          display: 'grid',
          gridTemplateColumns: '1.2fr 0.8fr',
          gap: '50px',
          alignItems: 'center',
          paddingTop: '80px',
          paddingBottom: '40px',
          marginTop: '-40px'
        }}>
          <motion.div 
            className="hero-content"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.h1 
              className="hero-title"
              style={{
                fontSize: 'clamp(2.4rem, 6vw, 4.5rem)',
                fontWeight: '900',
                lineHeight: '1.1',
                color: '#451A03',
                marginBottom: '24px',
                letterSpacing: '-1.5px'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Kafenizi <span className="highlight">SobCof</span> ile Buluşturun
            </motion.h1>
            
            <motion.p 
              className="hero-description"
              style={{
                fontSize: 'clamp(1.1rem, 2vw, 1.35rem)',
                color: '#78350F',
                lineHeight: '1.6',
                marginBottom: '40px',
                fontWeight: '500'
              }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Türkiye'nin en popüler kahve sosyalleşme platformunda yerinizi alın.
              Kafenizi binlerce kahve severin keşfetmesini sağlayın ve müşteri portföyünüzü genişletin.
            </motion.p>
            
            <motion.div 
              className="hero-buttons"
              style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '30px' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <motion.a 
                href="#benefits" 
                className="btn-primary"
                whileHover={{ scale: 1.05, translateY: -3 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  background: 'linear-gradient(135deg, #78350F 0%, #D2691E 100%)',
                  boxShadow: '0 10px 30px rgba(120,53,15,0.25)'
                }}
              >
                <i className="fas fa-chart-line"></i>
                Avantajları Keşfet
              </motion.a>
              <motion.a 
                href="#application-form" 
                className="btn-secondary"
                whileHover={{ scale: 1.05, translateY: -3 }}
                whileTap={{ scale: 0.98 }}
              >
                <i className="fas fa-handshake"></i>
                Hemen Başvur
              </motion.a>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <a href="/cafe-partnership/login" style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                color: '#78350F',
                fontSize: '1rem',
                fontWeight: '700',
                textDecoration: 'none',
                padding: '10px 16px',
                borderRadius: '8px',
                border: '2px solid #78350F',
                transition: 'all 0.3s ease',
                background: 'rgba(255,255,255,0.4)',
                backdropFilter: 'blur(8px)'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#78350F';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.4)';
                  e.currentTarget.style.color = '#78350F';
                }}
              >
                <i className="fas fa-sign-in-alt"></i>
                Partner Kafeyseniz Giriş Yapın
              </a>
            </motion.div>
          </motion.div>
          
          {/* Hero Visual Mockup: Super striking titled iPhone 15 Pro displaying cafe detail */}
          <div className="hero-visual" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: 2 }}
              animate={{ opacity: 1, scale: 1, rotate: -4 }}
              transition={{ duration: 1, type: "spring", stiffness: 80 }}
              style={{ position: 'relative' }}
            >
              <RealisticIPhone imgSrc="/assets/cafe_detail.png" label="SobCof Uygulamasında Kafeniz" />
              
              {/* Dynamic decorative glass floaters surrounding the phone */}
              <motion.div
                style={{
                  position: 'absolute',
                  top: '-40px',
                  right: '-60px',
                  background: 'rgba(255, 255, 255, 0.85)',
                  padding: '12px 18px',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(120,53,15,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  zIndex: 5
                }}
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
              >
                <i className="fas fa-mobile-alt" style={{ color: '#D2691E' }}></i>
                <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#451A03' }}>Uygulamada Görünürlük</span>
              </motion.div>

              <motion.div
                style={{
                  position: 'absolute',
                  bottom: '20px',
                  left: '-80px',
                  background: 'rgba(255, 255, 255, 0.85)',
                  padding: '12px 18px',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(120,53,15,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  zIndex: 5
                }}
                animate={{ y: [0, 8, 0] }}
                transition={{ repeat: Infinity, duration: 4, ease: "easeInOut", delay: 0.5 }}
              >
                <i className="fas fa-qrcode" style={{ color: '#78350F' }}></i>
                <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#451A03' }}>QR Menü Sistemi</span>
              </motion.div>

              <motion.div
                style={{
                  position: 'absolute',
                  bottom: '-30px',
                  right: '-30px',
                  background: 'rgba(255, 255, 255, 0.85)',
                  padding: '12px 18px',
                  borderRadius: '16px',
                  boxShadow: '0 10px 30px rgba(120,53,15,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  zIndex: 5
                }}
                animate={{ y: [0, -8, 0] }}
                transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut", delay: 1 }}
              >
                <i className="fas fa-handshake" style={{ color: '#FBBF24' }}></i>
                <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#451A03' }}>Partner Rozeti</span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="benefits" style={{ backgroundColor: '#FAF8F5', padding: '100px 0' }}>
        <div className="container">
          <motion.div 
            className="section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Neden SobCof Partner Kafesi Olmalısınız?</h2>
            <p className="section-subtitle">
              Kafenizi Türkiye'nin en büyük kahve topluluğuna tanıtın ve işinizi büyütün
            </p>
          </motion.div>

          <motion.div 
            className="benefits-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.1
                }
              }
            }}
          >
            <motion.div 
              className="benefit-card glass-card"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
              }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="benefit-icon">
                <i className="fas fa-user-plus"></i>
              </div>
              <h3 className="benefit-title">Yeni Müşteri Kazanımı</h3>
              <p className="benefit-description">
                Aktif kullanıcılarımız arasından kafenizi keşfedecek yeni müşteriler kazanın.
                SobCof kullanıcıları lokasyon bazlı eşleşme arayanlar olduğu için kafenizi tercih edebilirler.
              </p>
            </motion.div>

            <motion.div 
              className="benefit-card glass-card"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
              }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="benefit-icon">
                <i className="fas fa-chart-line"></i>
              </div>
              <h3 className="benefit-title">Potansiyel Büyüme</h3>
              <p className="benefit-description">
                SobCof kullanıcıları kafelerde sosyalleşmeyi seven, uzun süre kalma
                eğiliminde olan müşterilerdir. Bu da satış potanyelinizi artırabilir.
              </p>
            </motion.div>

            <motion.div 
              className="benefit-card glass-card"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
              }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="benefit-icon">
                <i className="fas fa-bullhorn"></i>
              </div>
              <h3 className="benefit-title">Ücretsiz Pazarlama</h3>
              <p className="benefit-description">
                Kafeniz uygulamamızda ücretsiz olarak tanıtılacak. Sosyal medya paylaşımları,
                özel etkinlikler ve influencer işbirlikleriyle görünürlüğünüzü artırın.
              </p>
            </motion.div>

            <motion.div 
              className="benefit-card glass-card"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
              }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="benefit-icon">
                <i className="fas fa-heart"></i>
              </div>
              <h3 className="benefit-title">Müşteri Memnuniyeti</h3>
              <p className="benefit-description">
                SobCof kullanıcıları kaliteli hizmet ve atmosfer arayan bilinçli müşterilerdir.
                Pozitif deneyimler yaşayan müşteriler kafenizi arkadaşlarına da önerecektir.
              </p>
            </motion.div>

            <motion.div 
              className="benefit-card glass-card"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
              }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="benefit-icon">
                <i className="fas fa-star"></i>
              </div>
              <h3 className="benefit-title">Kalite Güvencesi</h3>
              <p className="benefit-description">
                SobCof kullanıcıları kaliteli kahve ve ambiyans arayan, bilinçli müşteriler.
                Marka değerinizi artıracak doğru müşteri kitlesiyle buluşun.
              </p>
            </motion.div>

            <motion.div 
              className="benefit-card glass-card"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
              }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="benefit-icon">
                <i className="fas fa-users"></i>
              </div>
              <h3 className="benefit-title">Gerçek Zamanlı Aktif Kullanıcı</h3>
              <p className="benefit-description">
                Kafenizide o anda kaç SobCof kullanıcısının bulunduğunu gerçek zamanlı olarak
                görebilirsiniz. Bu sayede kafenizin popülerliğini takip edebilirsiniz.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Real QR Menu Section with Ultra-Realistic iPhone */}
      <section className="qr-menu-section" style={{ backgroundColor: '#F3ECE3', padding: '100px 0' }}>
        <div className="container">
          <div className="qr-menu-content" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '80px', alignItems: 'center' }}>
            <motion.div 
              className="qr-menu-visual"
              initial={{ opacity: 0, x: -40, scale: 0.95 }}
              whileInView={{ opacity: 1, x: 0, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, type: "spring" }}
              style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}
            >
              <RealisticIPhone imgSrc="/assets/qr_menu.png" label="Kafenizin Gerçek QR Menüsü" />
              
              <motion.div 
                className="floating-qr-code"
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              >
                <i className="fas fa-qrcode"></i>
              </motion.div>
            </motion.div>
            
            <motion.div 
              className="qr-menu-info"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="section-title">Gerçek QR Menü Sistemi</h2>
              <p className="section-subtitle" style={{ marginBottom: '30px' }}>
                Müşterilerinize modern ve son derece hijyenik bir temassız menü deneyimi sunun.
              </p>
              
              <div className="qr-menu-benefits" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div className="qr-benefit glass-card" style={{ borderRadius: '16px', padding: '16px', display: 'flex', gap: '16px' }}>
                  <div className="qr-benefit-icon" style={{ fontSize: '1.5rem', color: '#78350F' }}>
                    <i className="fas fa-edit"></i>
                  </div>
                  <div className="qr-benefit-content">
                    <h4 style={{ fontWeight: 'bold', color: '#451A03' }}>Saniyeler İçinde Güncelleme</h4>
                    <p style={{ color: '#78350F', fontSize: '0.9rem' }}>Fiyat ve ürün güncellemelerinizi işletme panelinizden anında yapın, kağıt basım maliyetlerinden tamamen kurtulun.</p>
                  </div>
                </div>

                <div className="qr-benefit glass-card" style={{ borderRadius: '16px', padding: '16px', display: 'flex', gap: '16px' }}>
                  <div className="qr-benefit-icon" style={{ fontSize: '1.5rem', color: '#78350F' }}>
                    <i className="fas fa-images"></i>
                  </div>
                  <div className="qr-benefit-content">
                    <h4 style={{ fontWeight: 'bold', color: '#451A03' }}>Göz Alıcı Görseller & Detaylar</h4>
                    <p style={{ color: '#78350F', fontSize: '0.9rem' }}>Ürünlerinize ait yüksek çözünürlüklü iştah açıcı görseller ekleyin. Müşterileriniz kahvelerin tüm aromalarını detaylarıyla görsün.</p>
                  </div>
                </div>

                <div className="qr-benefit glass-card" style={{ borderRadius: '16px', padding: '16px', display: 'flex', gap: '16px' }}>
                  <div className="qr-benefit-icon" style={{ fontSize: '1.5rem', color: '#78350F' }}>
                    <i className="fas fa-leaf"></i>
                  </div>
                  <div className="qr-benefit-content">
                    <h4 style={{ fontWeight: 'bold', color: '#451A03' }}>Çevre Dostu ve Ekonomik</h4>
                    <p style={{ color: '#78350F', fontSize: '0.9rem' }}>Kağıt ve baskı israfını önleyerek kafenizin karbon ayak izini azaltın ve doğa dostu bir kimlik kazanın.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* User App Experience Showcase with Ultra-Realistic iPhones */}
      <section className="user-experience-section" style={{ padding: '100px 0', backgroundColor: '#FAF8F5' }}>
        <div className="container">
          <motion.div 
            className="section-header" 
            style={{ textAlign: 'center', marginBottom: '60px' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Kullanıcıların Gözünden Kafeniz</h2>
            <p className="section-subtitle">SobCof mobil uygulama kullanıcılarının kafenizi ve sadakat programınızı nasıl gördüğünü deneyimleyin</p>
          </motion.div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', alignItems: 'center' }} className="exp-grid-responsive">
            <div style={{ display: 'flex', gap: '30px', justifyContent: 'center', position: 'relative', paddingBottom: '40px' }} className="phone-stack-responsive">
              {/* Left Phone: Cafe Detail */}
              <RealisticIPhone imgSrc="/assets/cafe_detail.png" label="Kafe Profil Ekranı" rotateDeg={-4} />

              {/* Right Phone: Stamp Card */}
              <RealisticIPhone imgSrc="/assets/stamp_card.png" label="Dijital Damga Kartı" rotateDeg={4} marginTop="40px" />
            </div>

            <div className="experience-text" style={{ paddingLeft: '20px' }}>
              <h3 style={{ fontSize: '2rem', color: '#451A03', marginBottom: '20px', fontWeight: '800' }}>Uygulama İçi Mükemmel Görünürlük</h3>
              <p style={{ color: '#78350F', fontSize: '1.1rem', marginBottom: '24px', lineHeight: '1.7' }}>
                SobCof kullanıcıları uygulamanın harita ve keşfet ekranları üzerinden kafenizi anında bulur. Kafe detay sayfanızda çalışma saatlerinizden sunduğunuz imkanlara kadar her detayı inceleyebilirler.
              </p>
              
              <div className="experience-features-list" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div className="exp-item" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <i className="fas fa-check-circle" style={{ color: '#D2691E', fontSize: '1.3rem', marginTop: '3px' }}></i>
                  <div>
                    <h4 style={{ color: '#451A03', fontWeight: 'bold', fontSize: '1.1rem' }}>Müdavim Kazandıran Sadakat Programı</h4>
                    <p style={{ color: '#78350F', fontSize: '0.95rem' }}>Müşterileriniz her kahve aldığında telefonlarından dijital sadakat kartlarına kolayca damga toplar. Kağıt kart kaybetme derdine son verirken, geri gelme oranlarını katlarsınız.</p>
                  </div>
                </div>
                <div className="exp-item" style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                  <i className="fas fa-check-circle" style={{ color: '#D2691E', fontSize: '1.3rem', marginTop: '3px' }}></i>
                  <div>
                    <h4 style={{ color: '#451A03', fontWeight: 'bold', fontSize: '1.1rem' }}>Sosyal Keşif ve Konum Bazlı Görünürlük</h4>
                    <p style={{ color: '#78350F', fontSize: '0.95rem' }}>Kahve içmek, çalışmak veya sosyalleşmek isteyen aktif kullanıcılar kafenizin konumunu, sunduğu imkanları ve özel fırsatları keşfederek doğrudan size yönlenir.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {/* Partners Carousel */}
      {partners.length > 0 && (
        <section className="partners-section" style={{
          padding: '60px 20px',
          backgroundColor: '#fff',
          overflow: 'hidden'
        }}>
          <div className="container">
            <div className="section-header" style={{ marginBottom: '40px' }}>
              <h2 className="section-title" style={{ textAlign: 'center' }}>İş Ortaklarımız</h2>
              <p className="section-subtitle" style={{ textAlign: 'center' }}>
                Türkiye'nin önde gelen kahve zincirleri ve kafeleri ile partneriz
              </p>
            </div>

            <div
              className="partners-carousel-wrapper"
              style={{
                position: 'relative',
                overflow: 'hidden',
                padding: '20px 0'
              }}
            >
              <div
                className="partners-carousel"
                ref={(el) => {
                  if (el && !el.dataset.initialized) {
                    el.dataset.initialized = 'true';
                    let scrollAmount = 0;
                    let isHovering = false;
                    let isDragging = false;

                    setInterval(() => {
                      if (!isHovering && !isDragging) {
                        scrollAmount += 1;
                        if (scrollAmount >= el.scrollWidth / 2) {
                          scrollAmount = 0;
                        }
                        el.scrollLeft = scrollAmount;
                      }
                    }, 20);

                    el.addEventListener('mouseenter', () => { isHovering = true; });
                    el.addEventListener('mouseleave', () => { isHovering = false; });

                    let startX = 0;
                    let scrollLeft = 0;

                    el.addEventListener('mousedown', (e) => {
                      isDragging = true;
                      el.style.cursor = 'grabbing';
                      startX = e.pageX - el.offsetLeft;
                      scrollLeft = el.scrollLeft;
                    });

                    el.addEventListener('mousemove', (e) => {
                      if (!isDragging) return;
                      e.preventDefault();
                      const x = e.pageX - el.offsetLeft;
                      const walk = (x - startX) * 2;
                      el.scrollLeft = scrollLeft - walk;
                      scrollAmount = el.scrollLeft;
                    });

                    el.addEventListener('mouseup', () => {
                      isDragging = false;
                      el.style.cursor = 'grab';
                    });

                    el.addEventListener('mouseleave', () => {
                      isDragging = false;
                      el.style.cursor = 'grab';
                    });

                    // Touch events
                    el.addEventListener('touchstart', (e) => {
                      isHovering = true;
                      startX = e.touches[0].pageX - el.offsetLeft;
                      scrollLeft = el.scrollLeft;
                    });

                    el.addEventListener('touchmove', (e) => {
                      const x = e.touches[0].pageX - el.offsetLeft;
                      const walk = (x - startX) * 2;
                      el.scrollLeft = scrollLeft - walk;
                      scrollAmount = el.scrollLeft;
                    });

                    el.addEventListener('touchend', () => {
                      isHovering = false;
                    });
                  }
                }}
                style={{
                  display: 'flex',
                  gap: '30px',
                  overflowX: 'scroll',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  cursor: 'grab',
                  WebkitOverflowScrolling: 'touch'
                }}
              >
                {[...partners, ...partners].map((partner, index) => (
                  <div
                    key={`${partner.id}-${index}`}
                    className="partner-logo-item"
                    style={{
                      width: '100px',
                      height: '100px',
                      minWidth: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '8px',
                      flexShrink: 0
                    }}
                  >
                    <img
                      src={partner.logoBase64}
                      alt={partner.name}
                      style={{
                        width: '84px',
                        height: '84px',
                        objectFit: 'contain',
                        borderRadius: '50%',
                        border: '3px solid #e0e0e0',
                        backgroundColor: '#fff',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        pointerEvents: 'none'
                      }}
                      title={partner.name}
                      draggable={false}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <style>{`
            .partners-carousel::-webkit-scrollbar {
              display: none;
            }

            .partners-carousel-wrapper {
              -webkit-mask-image: linear-gradient(
                to right,
                transparent,
                black 5%,
                black 95%,
                transparent
              );
              mask-image: linear-gradient(
                to right,
                transparent,
                black 5%,
                black 95%,
                transparent
              );
            }

            @media (max-width: 768px) {
              .partner-logo-item {
                width: 80px !important;
                min-width: 80px !important;
                height: 80px !important;
              }
              .partner-logo-item img {
                width: 64px !important;
                height: 64px !important;
              }
            }
          `}</style>
        </section>
      )}

      {/* Partnership Process */}
      <section className="partnership-process" style={{ backgroundColor: '#FAF8F5' }}>
        <div className="container">
          <motion.div 
            className="section-header" 
            style={{ textAlign: 'center' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="section-title">Partnerlik Süreci</h2>
            <p className="section-subtitle">Sadece 4 kolay adımda SobCof ailesine katılın</p>
          </motion.div>

          <motion.div 
            className="process-steps-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.1
                }
              }
            }}
          >
            <motion.div 
              className="process-step-card glass-card"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { type: "spring" } }
              }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="step-number">1</div>
              <h3 className="step-title">Başvuru Formu</h3>
              <p className="step-description">
                Aşağıdaki sadeleştirilmiş formu 1 dakikada doldurarak kafenizi hemen sisteme kaydedin.
              </p>
            </motion.div>

            <motion.div 
              className="process-step-card glass-card"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { type: "spring" } }
              }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="step-number">2</div>
              <h3 className="step-title">Değerlendirme</h3>
              <p className="step-description">
                Ekibimiz başvurunuzu değerlendirir ve uygunluk durumunu 48 saat içinde size bildirir.
              </p>
            </motion.div>

            <motion.div 
              className="process-step-card glass-card"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { type: "spring" } }
              }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="step-number">3</div>
              <h3 className="step-title">Giriş Bilgileriniz</h3>
              <p className="step-description">
                Kafeniz onaylandıktan sonra, e-posta adresinize işletme paneli giriş bilgileriniz otomatik olarak gönderilir.
              </p>
            </motion.div>

            <motion.div 
              className="process-step-card glass-card"
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { type: "spring" } }
              }}
              whileHover={{ y: -8, scale: 1.02 }}
            >
              <div className="step-number">4</div>
              <h3 className="step-title">Yönetmeye Başlayın!</h3>
              <p className="step-description">
                Panelinize giriş yapıp kafe fotoğraflarınızı ekleyin, menünüzü girin ve kampanyalarınızı saniyeler içinde başlatın.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Simplified, Premium Application Form Section with Perfect Checkmark Display */}
      <section id="application-form" className="application-form-section" style={{ backgroundColor: '#FAF8F5', padding: '100px 0' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <div className="form-header" style={{ textAlign: 'center', marginBottom: '50px' }}>
            <h2 className="section-title">Kafe Partnerliği Başvurusu</h2>
            <p className="section-subtitle">
              Gereksiz detaylarla vakit kaybetmeden 1 dakikada hızlıca başvurun. Kurulumu yönetim panelinizden kolayca tamamlarsınız.
            </p>
          </div>

          <div className="form-container">
            {submitStatus === 'success' && (
              <motion.div 
                id="success-message" 
                className="alert alert-success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                style={{
                  backgroundColor: '#D1E7DD',
                  color: '#0F5132',
                  padding: '20px',
                  borderRadius: '12px',
                  marginBottom: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  border: '1px solid #BADBCC',
                  fontWeight: '600'
                }}
              >
                <i className="fas fa-check-circle" style={{ fontSize: '1.5rem' }}></i>
                <span>Başvurunuz başarıyla gönderildi! Ekibimiz 48 saat içinde sizinle iletişime geçecek.</span>
              </motion.div>
            )}

            {submitStatus === 'error' && (
              <div className="alert alert-error" style={{
                backgroundColor: '#F8D7DA',
                color: '#842029',
                padding: '20px',
                borderRadius: '12px',
                marginBottom: '30px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                border: '1px solid #F5C2C7',
                fontWeight: '600'
              }}>
                <i className="fas fa-exclamation-circle" style={{ fontSize: '1.5rem' }}></i>
                <span>Başvuru gönderilirken bir hata oluştu. Lütfen bilgilerinizi kontrol edip tekrar deneyin.</span>
              </div>
            )}

            <form className="partnership-form" onSubmit={handleSubmit}>
              {/* Cafe Information */}
              <div className="form-section glass-card" style={{ padding: '36px', borderRadius: '24px', border: '1px solid rgba(120,53,15,0.1)', background: 'rgba(255,255,255,0.85)', marginBottom: '30px' }}>
                <h3 className="form-section-title" style={{ fontSize: '1.5rem', color: '#451A03', fontWeight: 'bold', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <i className="fas fa-store" style={{ color: '#78350F' }}></i>
                  Kafe Bilgileri
                </h3>

                <div className="form-group" style={{ marginBottom: '20px' }}>
                  <label htmlFor="cafeName" style={{ display: 'block', fontWeight: '600', color: '#451A03', marginBottom: '8px' }}>
                    <i className="fas fa-coffee" style={{ marginRight: '8px', color: '#78350F' }}></i>
                    Kafe Adı *
                  </label>
                  <input
                    type="text"
                    id="cafeName"
                    name="cafeName"
                    value={formData.cafeName}
                    onChange={handleInputChange}
                    required
                    placeholder="Örn: Brew & Beans Coffee House"
                    className={errors.cafeName ? 'error-field' : ''}
                    style={{
                      width: '100%',
                      padding: '14px 18px',
                      borderRadius: '10px',
                      border: '1px solid rgba(120,53,15,0.2)',
                      background: '#FFF',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.3s ease'
                    }}
                  />
                  {errors.cafeName && <span className="error-message" style={{ color: '#DC3545', fontSize: '0.85rem', marginTop: '6px', display: 'block' }}>{errors.cafeName}</span>}
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div className="form-group">
                    <label htmlFor="city" style={{ display: 'block', fontWeight: '600', color: '#451A03', marginBottom: '8px' }}>
                      <i className="fas fa-city" style={{ marginRight: '8px', color: '#78350F' }}></i>
                      Şehir *
                    </label>
                    <select
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      required
                      className={errors.city ? 'error-field' : ''}
                      style={{
                        width: '100%',
                        padding: '14px 18px',
                        borderRadius: '10px',
                        border: '1px solid rgba(120,53,15,0.2)',
                        background: '#FFF',
                        fontSize: '1rem',
                        outline: 'none',
                        height: '52px',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      <option value="">Şehir seçin...</option>
                      <option value="istanbul">İstanbul</option>
                      <option value="ankara">Ankara</option>
                      <option value="izmir">İzmir</option>
                      <option value="bursa">Bursa</option>
                      <option value="antalya">Antalya</option>
                      <option value="adana">Adana</option>
                      <option value="gaziantep">Gaziantep</option>
                      <option value="konya">Konya</option>
                      <option value="mersin">Mersin</option>
                      <option value="kayseri">Kayseri</option>
                      <option value="eskisehir">Eskişehir</option>
                      <option value="diyarbakir">Diyarbakır</option>
                      <option value="samsun">Samsun</option>
                      <option value="denizli">Denizli</option>
                      <option value="sakarya">Sakarya</option>
                      <option value="van">Van</option>
                      <option value="kahramanmaras">Kahramanmaraş</option>
                      <option value="erzurum">Erzurum</option>
                      <option value="sanliurfa">Şanlıurfa</option>
                      <option value="malatya">Malatya</option>
                      <option value="trabzon">Trabzon</option>
                      <option value="manisa">Manisa</option>
                      <option value="balikesir">Balıkesir</option>
                      <option value="hatay">Hatay</option>
                      <option value="tekirdag">Tekirdağ</option>
                      <option value="aydin">Aydın</option>
                      <option value="mugla">Muğla</option>
                      <option value="ordu">Ordu</option>
                      <option value="rize">Rize</option>
                      <option value="Diğer">Diğer</option>
                    </select>
                    {errors.city && <span className="error-message" style={{ color: '#DC3545', fontSize: '0.85rem', marginTop: '6px', display: 'block' }}>{errors.city}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="district" style={{ display: 'block', fontWeight: '600', color: '#451A03', marginBottom: '8px' }}>
                      <i className="fas fa-map-signs" style={{ marginRight: '8px', color: '#78350F' }}></i>
                      İlçe *
                    </label>
                    <input
                      type="text"
                      id="district"
                      name="district"
                      value={formData.district}
                      onChange={handleInputChange}
                      required
                      placeholder="Örn: Kadıköy, Çankaya"
                      className={errors.district ? 'error-field' : ''}
                      style={{
                        width: '100%',
                        padding: '14px 18px',
                        borderRadius: '10px',
                        border: '1px solid rgba(120,53,15,0.2)',
                        background: '#FFF',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                    />
                    {errors.district && <span className="error-message" style={{ color: '#DC3545', fontSize: '0.85rem', marginTop: '6px', display: 'block' }}>{errors.district}</span>}
                  </div>
                </div>

                {formData.city === 'Diğer' && (
                  <div className="form-group" style={{ marginBottom: '20px' }}>
                    <label htmlFor="otherCity" style={{ display: 'block', fontWeight: '600', color: '#451A03', marginBottom: '8px' }}>
                      <i className="fas fa-edit" style={{ marginRight: '8px', color: '#78350F' }}></i>
                      Şehir Adı *
                    </label>
                    <input
                      type="text"
                      id="otherCity"
                      name="otherCity"
                      value={formData.otherCity}
                      onChange={handleInputChange}
                      placeholder="Lütfen şehir adını yazın"
                      className={errors.otherCity ? 'error-field' : ''}
                      required
                      style={{
                        width: '100%',
                        padding: '14px 18px',
                        borderRadius: '10px',
                        border: '1px solid rgba(120,53,15,0.2)',
                        background: '#FFF',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                    />
                    {errors.otherCity && <span className="error-message" style={{ color: '#DC3545', fontSize: '0.85rem', marginTop: '6px', display: 'block' }}>{errors.otherCity}</span>}
                  </div>
                )}

                <div className="form-group" style={{ marginBottom: '10px' }}>
                  <label htmlFor="address" style={{ display: 'block', fontWeight: '600', color: '#451A03', marginBottom: '8px' }}>
                    <i className="fas fa-map-marker-alt" style={{ marginRight: '8px', color: '#78350F' }}></i>
                    Kafe Adresi *
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    placeholder="Tam adres (cadde, sokak, no, mahalle)"
                    className={errors.address ? 'error-field' : ''}
                    style={{
                      width: '100%',
                      padding: '14px 18px',
                      borderRadius: '10px',
                      border: '1px solid rgba(120,53,15,0.2)',
                      background: '#FFF',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.3s ease'
                    }}
                  />
                  {errors.address && <span className="error-message" style={{ color: '#DC3545', fontSize: '0.85rem', marginTop: '6px', display: 'block' }}>{errors.address}</span>}
                </div>
              </div>

              {/* Authorized Representative Contact Information */}
              <div className="form-section glass-card" style={{ padding: '36px', borderRadius: '24px', border: '1px solid rgba(120,53,15,0.1)', background: 'rgba(255,255,255,0.85)', marginBottom: '30px' }}>
                <h3 className="form-section-title" style={{ fontSize: '1.5rem', color: '#451A03', fontWeight: 'bold', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <i className="fas fa-user" style={{ color: '#78350F' }}></i>
                  Yetkili ve İletişim Bilgileri
                </h3>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div className="form-group">
                    <label htmlFor="ownerName" style={{ display: 'block', fontWeight: '600', color: '#451A03', marginBottom: '8px' }}>
                      <i className="fas fa-user-circle" style={{ marginRight: '8px', color: '#78350F' }}></i>
                      Kafe Sahibi / Yetkili Ad Soyad *
                    </label>
                    <input
                      type="text"
                      id="ownerName"
                      name="ownerName"
                      value={formData.ownerName}
                      onChange={handleInputChange}
                      required
                      placeholder="Adınız ve soyadınız"
                      className={errors.ownerName ? 'error-field' : ''}
                      style={{
                        width: '100%',
                        padding: '14px 18px',
                        borderRadius: '10px',
                        border: '1px solid rgba(120,53,15,0.2)',
                        background: '#FFF',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                    />
                    {errors.ownerName && <span className="error-message" style={{ color: '#DC3545', fontSize: '0.85rem', marginTop: '6px', display: 'block' }}>{errors.ownerName}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" style={{ display: 'block', fontWeight: '600', color: '#451A03', marginBottom: '8px' }}>
                      <i className="fas fa-envelope" style={{ marginRight: '8px', color: '#78350F' }}></i>
                      E-posta Adresi *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="ornek@email.com"
                      className={errors.email ? 'error-field' : ''}
                      style={{
                        width: '100%',
                        padding: '14px 18px',
                        borderRadius: '10px',
                        border: '1px solid rgba(120,53,15,0.2)',
                        background: '#FFF',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                    />
                    {errors.email && <span className="error-message" style={{ color: '#DC3545', fontSize: '0.85rem', marginTop: '6px', display: 'block' }}>{errors.email}</span>}
                  </div>
                </div>

                <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div className="form-group">
                    <label htmlFor="phone" style={{ display: 'block', fontWeight: '600', color: '#451A03', marginBottom: '8px' }}>
                      <i className="fas fa-phone" style={{ marginRight: '8px', color: '#78350F' }}></i>
                      Telefon Numarası *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+90 (555) 123 45 67"
                      className={errors.phone ? 'error-field' : ''}
                      style={{
                        width: '100%',
                        padding: '14px 18px',
                        borderRadius: '10px',
                        border: '1px solid rgba(120,53,15,0.2)',
                        background: '#FFF',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                    />
                    {errors.phone && <span className="error-message" style={{ color: '#DC3545', fontSize: '0.85rem', marginTop: '6px', display: 'block' }}>{errors.phone}</span>}
                  </div>

                  <div className="form-group">
                    <label htmlFor="website" style={{ display: 'block', fontWeight: '600', color: '#451A03', marginBottom: '8px' }}>
                      <i className="fas fa-globe" style={{ marginRight: '8px', color: '#78350F' }}></i>
                      Website (Opsiyonel)
                    </label>
                    <input
                      type="url"
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="https://www.websiteniz.com"
                      style={{
                        width: '100%',
                        padding: '14px 18px',
                        borderRadius: '10px',
                        border: '1px solid rgba(120,53,15,0.2)',
                        background: '#FFF',
                        fontSize: '1rem',
                        outline: 'none',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  </div>
                </div>

                <div className="form-group" style={{ marginBottom: '10px' }}>
                  <label htmlFor="instagram" style={{ display: 'block', fontWeight: '600', color: '#451A03', marginBottom: '8px' }}>
                    <i className="fab fa-instagram" style={{ marginRight: '8px', color: '#78350F' }}></i>
                    Instagram Hesabı (Opsiyonel)
                  </label>
                  <input
                    type="text"
                    id="instagram"
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleInputChange}
                    placeholder="@kafenizin_hesabi"
                    style={{
                      width: '100%',
                      padding: '14px 18px',
                      borderRadius: '10px',
                      border: '1px solid rgba(120,53,15,0.2)',
                      background: '#FFF',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.3s ease'
                    }}
                  />
                </div>
              </div>

              {/* Consent checkmarks - FIXED Checkmark display bugs */}
              <div className="form-agreements" style={{ marginBottom: '30px', padding: '0 10px' }}>
                <div className="agreement-item" style={{ marginBottom: '18px', display: 'flex', flexDirection: 'column' }}>
                  <label className="checkbox-label" style={{ display: 'flex', gap: '12px', cursor: 'pointer', alignItems: 'flex-start', fontSize: '0.95rem', color: '#78350F' }}>
                    <input
                      type="checkbox"
                      id="privacy"
                      name="privacy"
                      checked={formData.privacy}
                      onChange={handleInputChange}
                    />
                    <span className="checkmark">
                      <i className="fas fa-check"></i>
                    </span>
                    <span className="agreement-text">
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#451A03', fontWeight: '600', textDecoration: 'underline' }}>
                        Gizlilik Politikası
                      </a>'nı ve{' '}
                      <a href="/partnership-terms" target="_blank" rel="noopener noreferrer" style={{ color: '#451A03', fontWeight: '600', textDecoration: 'underline' }}>
                        Partnerlik Koşulları
                      </a>'nı okudum ve kabul ediyorum *
                    </span>
                  </label>
                  {errors.privacy && <span className="error-message" style={{ color: '#DC3545', fontSize: '0.85rem', marginTop: '6px', display: 'block', paddingLeft: '32px' }}>{errors.privacy}</span>}
                </div>

                <div className="agreement-item">
                  <label className="checkbox-label" style={{ display: 'flex', gap: '12px', cursor: 'pointer', alignItems: 'flex-start', fontSize: '0.95rem', color: '#78350F' }}>
                    <input
                      type="checkbox"
                      id="marketing"
                      name="marketing"
                      checked={formData.marketing}
                      onChange={handleInputChange}
                    />
                    <span className="checkmark">
                      <i className="fas fa-check"></i>
                    </span>
                    <span className="agreement-text">
                      SobCof pazarlama kampanyalarında kafemi kullanmasına ve başarı hikayemi paylaşmasına izin veriyorum
                    </span>
                  </label>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className="form-actions" style={{ textAlign: 'center' }}>
                <button 
                  type="submit" 
                  className="submit-btn primary" 
                  disabled={isSubmitting}
                  style={{
                    background: 'linear-gradient(135deg, #78350F 0%, #D2691E 100%)',
                    color: '#fff',
                    padding: '16px 40px',
                    borderRadius: '50px',
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 8px 25px rgba(120,53,15,0.25)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '12px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="spinner" style={{
                        width: '20px',
                        height: '20px',
                        border: '3px solid rgba(255,255,255,0.3)',
                        borderRadius: '50%',
                        borderTopColor: '#fff',
                        animation: 'spin 1s ease-in-out infinite'
                      }}></div>
                      <span>Başvuru Gönderiliyor...</span>
                    </>
                  ) : (
                    <>
                      <i className="fas fa-handshake"></i>
                      <span>Partnerlik Başvurusu Gönder</span>
                    </>
                  )}
                </button>

                <div className="form-note" style={{ marginTop: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', color: '#78350F', fontSize: '0.9rem' }}>
                  <i className="fas fa-info-circle"></i>
                  <span>Başvurunuz 48 saat içinde değerlendirilecek ve size geri dönüş yapılacak</span>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-logo">
                <a href="/" className="logo-link">
                  <img src="/assets/LogoWhite.png" alt="SobCof Logo" />
                </a>
              </div>
              <p className="footer-description">
                Kahve severler için sosyal keşif platformu. En iyi kafeleri keşfet, yeni insanlarla tanış.
              </p>
              <div className="social-links">
                <a href="https://instagram.com/sobcof" title="Instagram" target="_blank" rel="noopener noreferrer"><i className="fab fa-instagram"></i></a>
                <a href="https://tiktok.com/@sobcof" title="TikTok" target="_blank" rel="noopener noreferrer"><i className="fab fa-tiktok"></i></a>
                <a href="https://twitter.com/sobcof" title="Twitter" target="_blank" rel="noopener noreferrer"><i className="fab fa-twitter"></i></a>
                <a href="https://youtube.com/@sobcof" title="YouTube" target="_blank" rel="noopener noreferrer"><i className="fab fa-youtube"></i></a>
              </div>
            </div>

            <div className="footer-section">
              <h4>Ürün</h4>
              <ul>
                <li><a href="/#features">Özellikler</a></li>
                <li><a href="/#premium">Premium</a></li>
                <li><a href="/#download">İndir</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Destek</h4>
              <ul>
                <li><a href="/help">Yardım Merkezi</a></li>
                <li><a href="/contact">İletişim</a></li>
                <li><a href="/feedback">Geri Bildirim</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Yasal</h4>
              <ul>
                <li><a href="/privacy">Gizlilik Politikası</a></li>
                <li><a href="/terms">Kullanım Koşulları</a></li>
                <li><a href="/cookies">Çerez Politikası</a></li>
                <li><a href="/gdpr">KVKK</a></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2026 SobCof. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>

      {/* Embedded CSS keyframes and advanced mobile responsiveness overrides */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .spinner {
          display: inline-block;
        }

        /* 📱 Flawless, Complete Mobile Responsiveness Overrides */
        @media (max-width: 991px) {
          .hero-container {
            grid-template-columns: 1fr !important;
            gap: 60px !important;
            text-align: center;
            padding-top: 20px;
          }
          .hero-content {
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          .hero-buttons {
            justify-content: center;
            width: 100%;
          }
          .hero-buttons .btn-primary,
          .hero-buttons .btn-secondary {
            width: 100%;
            justify-content: center;
          }
          .qr-menu-content {
            grid-template-columns: 1fr !important;
            gap: 60px !important;
            text-align: center;
          }
          .qr-menu-visual {
            order: 2;
          }
          .qr-menu-info {
            order: 1;
          }
          .dashboard-grid-responsive {
            grid-template-columns: 1fr !important;
            gap: 60px !important;
            text-align: center;
          }
        }

        @media (max-width: 768px) {
          .exp-grid-responsive {
            grid-template-columns: 1fr !important;
            gap: 60px !important;
          }
          .dashboard-grid-responsive {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          .phone-stack-responsive {
            margin-top: 20px;
            margin-bottom: 40px;
            flex-wrap: wrap;
            gap: 40px 20px !important;
          }
          .phone-stack-responsive .realistic-iphone-device {
            transform: rotate(0deg) !important;
            margin-top: 0 !important;
            width: 240px !important;
            height: 480px !important;
          }
          .experience-text {
            padding-left: 0 !important;
            text-align: center;
          }
          .experience-features-list {
            text-align: left;
          }
          .form-row {
            grid-template-columns: 1fr !important;
            gap: 0 !important;
          }
          .form-section {
            padding: 24px 16px !important;
          }
          .form-actions .submit-btn {
            width: 100%;
            justify-content: center;
          }
        }
      `}</style>
    </div>
  );
};

export default CafePartnershipPage;
